import { useEffect, useState } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    Toolbar,
    SaveButton,
    Button,
    required,
    useDataProvider,
    useNotify,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { normalizeCfStreamUid } from './validators';

const EditToolbar = () => {
    const navigate = useNavigate();
    return (
        <Toolbar>
            <SaveButton />
            <Button label="Cancel" onClick={() => navigate('/admin/kivoai-videos')} />
        </Toolbar>
    );
};

const StreamPreview = () => {
    const uidValue = useWatch({ name: 'uid' }) as string | undefined;
    const uid = normalizeCfStreamUid(uidValue);
    const iframeUrl = uid ? `https://iframe.videodelivery.net/${uid}` : '';

    return (
        <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
                Stream Preview
            </Typography>
            {iframeUrl ? (
                <Box
                    component="iframe"
                    src={iframeUrl}
                    title="Cloudflare Stream Preview"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    sx={{
                        width: '100%',
                        height: { xs: 240, sm: 380 },
                        border: 0,
                        borderRadius: 1,
                        backgroundColor: 'black',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        height: { xs: 120, sm: 140 },
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        fontSize: 14,
                    }}
                >
                    Fill a valid UID to preview the stream
                </Box>
            )}
        </Grid>
    );
};

const UidActions = () => {
    const notify = useNotify();
    const uidValue = useWatch({ name: 'uid' }) as string | undefined;
    const uid = normalizeCfStreamUid(uidValue);

    const openPlayer = () => {
        if (!uid) {
            notify('UID is empty', { type: 'warning' });
            return;
        }
        window.open(`https://iframe.videodelivery.net/${uid}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <Grid size={12}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    Open current stream in a new tab
                </Typography>
                <Tooltip title="Open player">
                    <span>
                        <IconButton size="small" onClick={openPlayer} disabled={!uid}>
                            <OpenInNewIcon fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Grid>
    );
};

const KivoaiVideoEditForm = () => {
    const dataProvider = useDataProvider();
    const [coachChoices, setCoachChoices] = useState<Array<any>>([]);

    useEffect(() => {
        let mounted = true;
        dataProvider
            .getList('admin/dropdown/coaches', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: '', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => {
                if (mounted) setCoachChoices(data || []);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, [dataProvider]);

    return (
        <SimpleForm toolbar={<EditToolbar />}>
            <Grid container spacing={2} sx={{ width: '100%', maxWidth: 800 }}>
                <Grid size={12}>
                    <Typography variant="h6" gutterBottom>
                        Edit KivoAI Video
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput source="id" label="ID" disabled fullWidth />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput source="title" label="Title" validate={required()} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectInput
                        source="userId"
                        label="Coach"
                        choices={coachChoices}
                        validate={required()}
                        fullWidth
                    />
                </Grid>

                <Grid size={12}>
                    <TextInput
                        source="description"
                        label="Description"
                        multiline
                        rows={3}
                        fullWidth
                    />
                </Grid>

                <UidActions />
                <StreamPreview />
            </Grid>
        </SimpleForm>
    );
};

const KivoaiVideoEdit = () => (
    <Edit>
        <KivoaiVideoEditForm />
    </Edit>
);

export default KivoaiVideoEdit;
